import Select from 'react-select'
import PropTypes from 'prop-types'
import { useLocation } from '../../contexts/LocationContext'
import { useLocationHierarchy } from '../../hooks/useLocationHierarchy'
import { useConfig } from '../../contexts/ConfigContext'

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    fontSize: '14px',
    borderColor: state.isFocused ? '#0ea5e9' : '#d4d4d4',
    boxShadow: state.isFocused ? '0 0 0 1px #0ea5e9' : 'none',
    '&:hover': {
      borderColor: '#a3a3a3',
    },
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected
      ? '#0ea5e9'
      : state.isFocused
        ? '#f0f9ff'
        : 'white',
    color: state.isSelected ? 'white' : '#171717',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    color: '#a3a3a3',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '14px',
    color: '#171717',
  }),
}

function LocationDropdown({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Select...',
}) {
  const selectOptions = options.map(option => ({
    value: option.id,
    label: option.name,
  }))
  const selectedOption =
    selectOptions.find(option => option.value === value) || null

  return (
    <div>
      <label className="block text-sm font-medium text-surface-700 mb-2">
        {label}
      </label>
      <Select
        value={selectedOption}
        onChange={option => onChange(option)}
        options={selectOptions}
        placeholder={placeholder}
        isClearable
        isDisabled={disabled}
        styles={customSelectStyles}
        className="text-sm"
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </div>
  )
}

LocationDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
}

function LocationSelector() {
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    setDistrict,
    setSubcounty,
    setParish,
    setVillage,
    reset,
    setAllDistricts,
    isViewingAllDistricts,
    isMultiDistrictMode,
    isSingleDistrictMode,
  } = useLocation()

  const {
    availableDistricts,
    availableSubcounties,
    availableParishes,
    availableVillages,
    selectedDistrictData,
    selectedSubcountyData,
    selectedParishData,
  } = useLocationHierarchy()

  const { getLocationDepthOrder } = useConfig()

  const handleDistrictChange = option => {
    if (option) {
      if (option.value === 'all') {
        setAllDistricts()
      } else {
        setDistrict(option.value)
      }
    } else {
      if (isMultiDistrictMode) {
        setAllDistricts()
      } else {
        reset()
      }
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleSubcountyChange = option => {
    if (option && selectedDistrictData) {
      setSubcounty(option.value)
    } else {
      setDistrict(selectedDistrict) // Reset to district level
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleParishChange = option => {
    if (option && selectedSubcountyData) {
      setParish(option.value)
    } else {
      setSubcounty(selectedSubcounty)
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleVillageChange = option => {
    if (option && selectedParishData) {
      setVillage(option.value)
    } else {
      setParish(selectedParish)
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  // Generate district options with "All Districts" for multi-district mode
  const getDistrictOptions = () => {
    const options = [...availableDistricts]
    if (isMultiDistrictMode) {
      options.unshift({ id: 'all', name: 'All Districts' })
    }
    return options
  }

  // Get current district value for the dropdown
  const getCurrentDistrictValue = () => {
    if (isViewingAllDistricts()) return 'all'
    return selectedDistrict
  }

  // Generate breadcrumb path
  const generateBreadcrumb = () => {
    const parts = []

    if (isViewingAllDistricts()) {
      parts.push('All Districts')
    } else if (selectedDistrictData) {
      parts.push(selectedDistrictData.name)
    }

    if (selectedSubcountyData) parts.push(selectedSubcountyData.name)
    if (selectedParishData) parts.push(selectedParishData.name)
    if (selectedVillage) {
      const villageData = availableVillages.find(v => v.id === selectedVillage)
      if (villageData) parts.push(villageData.name)
    }
    return parts.join(' → ')
  }

  // Single District Mode Display Component
  if (isSingleDistrictMode) {
    const allowedDepths = getLocationDepthOrder()

    return (
      <div className="space-y-4">
        {/* Sub-location Dropdowns (starting from subcounty) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {allowedDepths.includes('subcounty') && (
            <LocationDropdown
              label="Subcounty"
              value={selectedSubcounty}
              options={availableSubcounties}
              onChange={handleSubcountyChange}
              placeholder="All Subcounties"
            />
          )}

          {allowedDepths.includes('parish') && (
            <LocationDropdown
              label="Parish"
              value={selectedParish}
              options={availableParishes}
              onChange={handleParishChange}
              disabled={!selectedSubcounty}
              placeholder={
                selectedSubcounty ? 'All Parishes' : 'Select Subcounty first'
              }
            />
          )}

          {allowedDepths.includes('village') && (
            <LocationDropdown
              label="Village"
              value={selectedVillage}
              options={availableVillages}
              onChange={handleVillageChange}
              disabled={!selectedParish}
              placeholder={
                selectedParish ? 'All Villages' : 'Select Parish first'
              }
            />
          )}
        </div>

        {/* Breadcrumb and Reset */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Current Location:</span>
            <span className="text-sm font-medium text-gray-900">
              {generateBreadcrumb()}
            </span>
          </div>

          {(selectedSubcounty || selectedParish || selectedVillage) && (
            <button
              onClick={() => {
                reset()
                // Scroll to main content area (just below the main selector panel)
                window.requestAnimationFrame(() => {
                  window.requestAnimationFrame(() => {
                    window.scrollTo({ top: 280, behavior: 'smooth' })
                  })
                })
              }}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>
    )
  }

  // Multi-District Mode Display Component
  const allowedDepths = getLocationDepthOrder()
  const gridColumns =
    allowedDepths.length > 3
      ? 'md:grid-cols-4'
      : `md:grid-cols-${allowedDepths.length}`

  return (
    <div className="space-y-4">
      {/* Location Dropdowns */}
      <div className={`grid grid-cols-1 ${gridColumns} gap-4`}>
        {allowedDepths.includes('district') && (
          <LocationDropdown
            label="District"
            value={getCurrentDistrictValue()}
            options={getDistrictOptions()}
            onChange={handleDistrictChange}
            placeholder="Select District"
          />
        )}

        {allowedDepths.includes('subcounty') && (
          <LocationDropdown
            label="Subcounty"
            value={selectedSubcounty}
            options={availableSubcounties}
            onChange={handleSubcountyChange}
            disabled={!selectedDistrict || isViewingAllDistricts()}
            placeholder={
              isViewingAllDistricts()
                ? 'Select a district first'
                : selectedDistrict
                  ? 'All Subcounties'
                  : 'Select District first'
            }
          />
        )}

        {allowedDepths.includes('parish') && (
          <LocationDropdown
            label="Parish"
            value={selectedParish}
            options={availableParishes}
            onChange={handleParishChange}
            disabled={!selectedSubcounty || isViewingAllDistricts()}
            placeholder={
              isViewingAllDistricts()
                ? 'Select a district first'
                : selectedSubcounty
                  ? 'All Parishes'
                  : 'Select Subcounty first'
            }
          />
        )}

        {allowedDepths.includes('village') && (
          <LocationDropdown
            label="Village"
            value={selectedVillage}
            options={availableVillages}
            onChange={handleVillageChange}
            disabled={!selectedParish || isViewingAllDistricts()}
            placeholder={
              isViewingAllDistricts()
                ? 'Select a district first'
                : selectedParish
                  ? 'All Villages'
                  : 'Select Parish first'
            }
          />
        )}
      </div>

      {/* Breadcrumb and Reset */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Current Location:</span>
          <span className="text-sm font-medium text-gray-900">
            {generateBreadcrumb() || 'All Locations'}
          </span>
        </div>

        {(selectedDistrict ||
          selectedSubcounty ||
          selectedParish ||
          selectedVillage ||
          isViewingAllDistricts()) && (
          <button
            onClick={() => {
              reset()
              // Scroll to main content area (just below the main selector panel)
              window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                  window.scrollTo({ top: 280, behavior: 'smooth' })
                })
              })
            }}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            {isViewingAllDistricts() ? 'Reset to Default' : 'Clear Selection'}
          </button>
        )}
      </div>
    </div>
  )
}

export default LocationSelector
